import { ArrowUpRight, Home, Banknote } from "lucide-react";
import { SITE, IMAGES } from "@/lib/site";

const CARDS = [
  {
    eyebrow: "The Search",
    title: "Real Estate",
    org: SITE.affiliations.realty,
    body:
      "Find the right home across Okaloosa, Walton & Santa Rosa Counties — Destin, 30A, Fort Walton Beach, Niceville, Crestview and everywhere in between. Listing, buying, investment, fully represented.",
    image: IMAGES.realEstate,
    cta: { label: "View My Active Listings", href: SITE.homesAgentUrl, external: true },
    icon: Home,
    chips: ["Okaloosa", "Walton", "Santa Rosa"],
  },
  {
    eyebrow: "The Strategy",
    title: "Mortgage",
    org: SITE.affiliations.mortgage,
    body:
      "Licensed in all 50 U.S. states + Puerto Rico, with a team that serves clients in 20 different languages. From everyday FHA/VA/Conventional to specialty Non-QM products — DSCR, Bank Statement Loans, HELOCs and beyond. Many lenders. One broker. The right loan.",
    image: IMAGES.mortgage,
    cta: { label: "Apply for a Mortgage", href: SITE.mortgageApplyUrl, external: true },
    icon: Banknote,
    chips: ["Conventional", "FHA", "VA", "Jumbo", "DSCR", "Bank Statement", "HELOC", "Non-QM"],
  },
];

export default function TwoSides() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-sand"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-2xl">
          <p className="overline text-ink" data-testid="services-overline">
            Services
          </p>
          <h2
            data-testid="services-heading"
            className="mt-5 font-display text-ink text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight"
          >
            Two sides of your
            <br />
            <span className="italic">home journey.</span>
          </h2>
          <p className="mt-6 text-text2/80 text-base md:text-lg leading-relaxed">
            Most buyers juggle a Realtor and a separate lender. With me, the strategy is
            unified — pricing, financing, and timing all move together.
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {CARDS.map((c) => {
            const Icon = c.icon;
            return (
              <a
                key={c.title}
                href={c.cta.href}
                target={c.cta.external ? "_blank" : undefined}
                rel={c.cta.external ? "noopener noreferrer" : undefined}
                data-testid={`service-card-${c.title.toLowerCase().replace(/\s/g, "-")}`}
                className="group relative min-h-[560px] rounded-2xl overflow-hidden block"
              >
                <img
                  src={c.image}
                  alt={c.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-ink/40 to-ink/10 group-hover:from-ink/95 transition-colors duration-500" />
                <div className="absolute top-6 left-6 flex items-center gap-3">
                  <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/15 backdrop-blur-md border border-white/20">
                    <Icon className="w-4 h-4 text-white" />
                  </span>
                  <span className="overline text-white/80">{c.eyebrow}</span>
                </div>

                <div className="absolute inset-x-0 bottom-0 p-8">
                  <div className="overline text-brass">{c.org}</div>
                  <h3 className="font-display text-white text-4xl md:text-5xl mt-2 tracking-tight">
                    {c.title}
                  </h3>
                  <p className="mt-4 text-white/85 text-sm md:text-base max-w-md leading-relaxed">
                    {c.body}
                  </p>
                  {c.chips && (
                    <div className="mt-5 flex flex-wrap gap-2">
                      {c.chips.map((chip) => (
                        <span
                          key={chip}
                          className="text-[11px] uppercase tracking-widest text-white/85 border border-white/25 rounded-full px-3 py-1 backdrop-blur-sm bg-white/5"
                        >
                          {chip}
                        </span>
                      ))}
                    </div>
                  )}
                  <span className="mt-6 inline-flex items-center gap-2 text-white text-sm font-medium border-b border-white/40 pb-1 transition-all duration-300 group-hover:gap-3 group-hover:border-brass">
                    {c.cta.label}
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
}
